import { Stack } from '@mantine/core';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import DeviceSection from 'components/forms/revision/fill-out/devices/DeviceSection';
import { useCallback, useMemo } from 'react';
import { useFillOutRevisionSaveDraftProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionSaveDraftProvider';
import panic from 'errors/panic';
import { useApi } from 'api/api-context';
import { useConfirm } from 'components/modals/message/MessageProvider';
import useInternalReload from 'hooks/use-internal-reload';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * Content of the devices tab in the Electric revision form
 */
export default function DevicesTabContent() {
  const { isTablet } = useResponsiveDesign();
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const { form, revision } = useFillOutRevisionDataProvider();
  const { saveDraft } = useFillOutRevisionSaveDraftProvider();
  const reload = useInternalReload();

  const remoteDevices = useMemo(
    () => revision.devices.filter(({ remoteSource, externalId }) => !!remoteSource && !!externalId),
    [revision.devices]
  );

  const visibleDevices = useMemo(
    () => Object.values(form.values.devices).filter(({ removed }) => !removed),
    [form.values.devices]
  );

  const parentDevice = useMemo(() => visibleDevices.find((device) => device.fields._isParentDevice), [visibleDevices]);
  const otherDevices = useMemo(
    () => visibleDevices.filter((device) => !device.fields._isParentDevice),
    [visibleDevices]
  );

  const sortedDevices = useMemo(
    () => (parentDevice ? [parentDevice, ...otherDevices] : otherDevices),
    [parentDevice, otherDevices]
  );

  const syncRemoteDevices = useCallback(async () => {
    try {
      const syncDevice = getAction('RemoteSourceSyncDevice');

      await saveDraft({ notify: false });

      for (const device of remoteDevices) {
        await syncDevice({
          parameters: {
            deviceRemoteId: device.externalId!,
            remoteSourceId: String(device.remoteSource!.remoteSourceId),
          },
        });
      }

      reload();
    } catch (error: any) {
      panic(error);
    }
  }, [getAction, remoteDevices, saveDraft, reload]);

  const confirmSyncRemoteDevices = useCallback(
    () =>
      confirm({
        title: 'Synchronizovať zariadenia',
        content: `Všetky zariadenia so vzdialeným zdrojom (${remoteDevices.length}) budú synchronizované.`,
        primaryButtonText: 'Synchronizovať',
        secondaryButtonText: 'Zrušiť',
        onConfirm: syncRemoteDevices,
      }),
    [confirm, syncRemoteDevices, remoteDevices.length]
  );

  return (
    <Stack spacing={isTablet ? 20 : 40}>
      {sortedDevices.map((device) => (
        <DeviceSection
          key={device.deviceId}
          device={device}
          onRemoteSourceSync={confirmSyncRemoteDevices}
          hasParentDevice={!!parentDevice}
        />
      ))}
    </Stack>
  );
}
